import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "./Sidebar";
import Header from "./Header";
import Notification from "./Notification";
import LoadingScreen from "./LoadingScreen";
import {
  FiArrowLeft,
  FiUser,
  FiUsers,
  FiFolder,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiX,
  FiSearch,
  FiRefreshCw,
  FiShield,
  FiMail,
  FiPhone,
  FiExternalLink,
  FiCopy,
  FiCheck,
  FiLayers,
  FiChevronRight,
} from "react-icons/fi";

const CustomerDetail = () => {
  const { company_id } = useParams();
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 1024 : true));
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  // Customer data
  const [customer, setCustomer] = useState(null);

  // Tab state
  const [activeTab, setActiveTab] = useState("users"); // "users" | "groups" | "projects"

  // Lists
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [projects, setProjects] = useState([]);

  // Search queries
  const [userSearch, setUserSearch] = useState("");
  const [groupSearch, setGroupSearch] = useState("");
  const [projectSearch, setProjectSearch] = useState("");

  // User Modals
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [newUser, setNewUser] = useState({
    full_name: "",
    username: "",
    email: "",
    phone: "",
    password: "",
    role: "Customer Admin",
  });
  const [editUser, setEditUser] = useState({
    id_user: "",
    full_name: "",
    username: "",
    email: "",
    phone: "",
    password: "",
    role: "Customer",
  });

  // Group Modals
  const [isAddGroupModalOpen, setIsAddGroupModalOpen] = useState(false);
  const [isEditGroupModalOpen, setIsEditGroupModalOpen] = useState(false);
  const [isDeleteGroupModalOpen, setIsDeleteGroupModalOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [newGroup, setNewGroup] = useState({ group_name: "" });
  const [editGroup, setEditGroup] = useState({ group_id: "", group_name: "" });

  // Sync state
  const [isSyncingProjects, setIsSyncingProjects] = useState(false);
  const [copiedBilling, setCopiedBilling] = useState(false);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Fetch all data
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const authHeaders = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      // 1. Fetch Customer
      let customerData = null;
      try {
        const custRes = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/customers/${company_id}`,
          authHeaders
        );
        customerData = custRes.data;
      } catch (err) {
        // Fallback: list all customers and find by company_id
        const allCustRes = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/customers/`,
          authHeaders
        );
        customerData = allCustRes.data.find((c) => c.company_id === company_id);
      }
      setCustomer(customerData);

      // 2. Fetch Users, Groups, Projects in parallel
      const [usersRes, groupsRes, projectsRes] = await Promise.all([
        axios
          .get(`${process.env.REACT_APP_API_BASE_URL}/api/users/company/${company_id}`, authHeaders)
          .catch(() => ({ data: [] })),
        axios
          .get(`${process.env.REACT_APP_API_BASE_URL}/api/groups/company/${company_id}`, authHeaders)
          .catch(() => ({ data: [] })),
        axios
          .get(`${process.env.REACT_APP_API_BASE_URL}/api/projects/company/${company_id}`, authHeaders)
          .catch(() => ({ data: [] })),
      ]);

      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
      setProjects(Array.isArray(projectsRes.data) ? projectsRes.data : []);
    } catch (error) {
      console.error("Error fetching customer details:", error);
      setNotification({
        type: "error",
        message: "Failed to load customer details. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [company_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedBilling(true);
    setTimeout(() => setCopiedBilling(false), 2000);
  };

  // ===================== USER ACTIONS =====================
  const handleAddUserSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...newUser,
        company_id: company_id,
        company_name: customer?.company_name || "",
        billing_account_id: customer?.billing_account_id || "",
      };
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/users/`, payload);
      setNotification({ type: "success", message: "User created successfully!" });
      setIsAddUserModalOpen(false);
      setNewUser({
        full_name: "",
        username: "",
        email: "",
        phone: "",
        password: "",
        role: "Customer Admin",
      });
      // Refresh users
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/company/${company_id}`);
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setNotification({
        type: "error",
        message: error?.response?.data?.detail || error?.response?.data?.message || "Failed to create user.",
      });
    }
  };

  const handleEditUserSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        full_name: editUser.full_name,
        username: editUser.username,
        email: editUser.email,
        phone: editUser.phone,
        role: editUser.role,
        company_id: company_id,
        company_name: customer?.company_name || "",
      };
      if (editUser.password && editUser.password.trim() !== "") {
        payload.password = editUser.password;
      }
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/users/${editUser.id_user}`, payload);
      setNotification({ type: "success", message: "User updated successfully!" });
      setIsEditUserModalOpen(false);
      // Refresh users
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/company/${company_id}`);
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setNotification({
        type: "error",
        message: error?.response?.data?.detail || error?.response?.data?.message || "Failed to update user.",
      });
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/users/${userToDelete.id_user}`);
      setNotification({ type: "success", message: "User deleted successfully!" });
      setUsers((prev) => prev.filter((u) => u.id_user !== userToDelete.id_user));
      setIsDeleteUserModalOpen(false);
      setUserToDelete(null);
    } catch (error) {
      setNotification({
        type: "error",
        message: error?.response?.data?.detail || error?.response?.data?.message || "Failed to delete user.",
      });
    }
  };

  // ===================== GROUP ACTIONS =====================
  const handleAddGroupSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        group_name: newGroup.group_name,
        company_id: company_id,
      };
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/groups/`, payload);
      setNotification({ type: "success", message: "Group created successfully!" });
      setIsAddGroupModalOpen(false);
      setNewGroup({ group_name: "" });
      // Refresh groups
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/groups/company/${company_id}`);
      setGroups(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setNotification({
        type: "error",
        message: error?.response?.data?.detail || error?.response?.data?.message || "Failed to create group.",
      });
    }
  };

  const handleEditGroupSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        group_name: editGroup.group_name,
        company_id: company_id,
      };
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/groups/${editGroup.group_id}`, payload);
      setNotification({ type: "success", message: "Group updated successfully!" });
      setIsEditGroupModalOpen(false);
      // Refresh groups
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/groups/company/${company_id}`);
      setGroups(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setNotification({
        type: "error",
        message: error?.response?.data?.detail || error?.response?.data?.message || "Failed to update group.",
      });
    }
  };

  const handleDeleteGroup = async () => {
    if (!groupToDelete) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/groups/${groupToDelete.group_id}`);
      setNotification({ type: "success", message: "Group deleted successfully!" });
      setGroups((prev) => prev.filter((g) => g.group_id !== groupToDelete.group_id));
      setIsDeleteGroupModalOpen(false);
      setGroupToDelete(null);
    } catch (error) {
      setNotification({
        type: "error",
        message: error?.response?.data?.detail || error?.response?.data?.message || "Failed to delete group.",
      });
    }
  };

  // ===================== PROJECT SYNC =====================
  const handleSyncProjects = async () => {
    if (!customer?.billing_account_id) {
      setNotification({
        type: "error",
        message: "Customer does not have a Billing Account ID configured for GCP sync.",
      });
      return;
    }
    setIsSyncingProjects(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/projects/${customer.billing_account_id}`
      );
      setNotification({ type: "success", message: "Projects synced from Google Cloud successfully!" });
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/projects/company/${company_id}`
      );
      setProjects(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setNotification({
        type: "error",
        message: error?.response?.data?.detail || error?.response?.data?.message || "Failed to sync projects from GCP.",
      });
    } finally {
      setIsSyncingProjects(false);
    }
  };

  // Filtered data
  const filteredUsers = users.filter((u) => {
    const q = userSearch.toLowerCase();
    return (
      (u.full_name || "").toLowerCase().includes(q) ||
      (u.username || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.role || "").toLowerCase().includes(q)
    );
  });

  const filteredGroups = groups.filter((g) => {
    const q = groupSearch.toLowerCase();
    return (
      (g.group_name || "").toLowerCase().includes(q) ||
      (g.group_id || "").toLowerCase().includes(q)
    );
  });

  const filteredProjects = projects.filter((p) => {
    const q = projectSearch.toLowerCase();
    return (
      (p.project_name || "").toLowerCase().includes(q) ||
      (p.project_id || "").toLowerCase().includes(q)
    );
  });

  const getRoleBadge = (role) => {
    switch (role) {
      case "Admin":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "Customer Admin":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "Customer":
        return "bg-emerald-100 text-emerald-800 border-emerald-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const getSLABadge = (sla) => {
    switch (sla) {
      case "7x24 Hours":
        return "bg-emerald-100 text-emerald-800 border-emerald-300";
      case "5x8 Hours":
        return "bg-blue-100 text-blue-800 border-blue-300";
      default:
        return "bg-amber-100 text-amber-800 border-amber-300";
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />
        <div className="flex flex-1 bg-gray-100 min-w-0 max-w-full overflow-x-hidden">
          <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
          <div className={`flex-1 min-w-0 max-w-full overflow-x-hidden p-3 sm:p-6 transition-all duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-20"} ml-0`}>
            <div className="pt-20 pb-6">
              <LoadingScreen />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />
      <div className="flex flex-1 bg-gray-100 min-w-0 max-w-full overflow-x-hidden">
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className={`flex-1 min-w-0 max-w-full overflow-x-hidden p-3 sm:p-6 transition-all duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-20"} ml-0`}>
          <div className="pt-16 sm:pt-20 pb-6">
            <div className="max-w-7xl mx-auto space-y-6">
              {/* Page Header / Breadcrumb matching Group Details */}
              <div className="mb-4 sm:mb-6">
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <button
                    onClick={() => navigate("/customer-management")}
                    className="hover:text-blue-600 font-medium transition-colors"
                  >
                    Customers
                  </button>
                  <FiChevronRight className="w-4 h-4 mx-2 text-gray-400" />
                  <span className="text-gray-900 font-semibold">{customer?.company_name || company_id}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                      Customer Details: {customer?.company_name || company_id}
                    </h1>
                    <p className="text-gray-600 text-sm mt-1">
                      Manage users, groups, and GCP projects for this customer
                    </p>
                  </div>
                  <button
                    onClick={() => navigate("/customer-management")}
                    className="self-start sm:self-auto inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-blue-600 transition-colors bg-white px-3.5 py-2 rounded-lg border border-gray-200 shadow-sm"
                  >
                    <FiArrowLeft className="w-4 h-4" />
                    Back to Customers
                  </button>
                </div>
              </div>

            {/* Customer Banner Header */}
            {customer && (
              <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-blue-800 text-white p-6 sm:p-8">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                          {customer.company_name}
                        </h1>
                        <span
                          className={`inline-flex px-2.5 py-0.5 text-xs font-bold rounded-full border bg-white/10 text-white border-white/20`}
                        >
                          {customer.maintenance || "5x8 Hours"}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-2 text-xs text-blue-100">
                        <div className="flex items-center gap-1.5">
                          <span className="text-blue-200">Billing ID:</span>
                          <span className="font-mono font-semibold bg-white/15 px-2 py-0.5 rounded text-white">
                            {customer.billing_account_id || "Not configured"}
                          </span>
                          {customer.billing_account_id && (
                            <button
                              onClick={() => copyToClipboard(customer.billing_account_id)}
                              className="text-blue-200 hover:text-white transition-colors"
                              title="Copy Billing ID"
                            >
                              {copiedBilling ? <FiCheck className="w-3.5 h-3.5 text-emerald-300" /> : <FiCopy className="w-3.5 h-3.5" />}
                            </button>
                          )}
                        </div>
                        <div>
                          <span className="text-blue-200">Company ID:</span>{" "}
                          <span className="font-mono text-white font-medium">{customer.company_id}</span>
                        </div>
                      </div>
                    </div>

                    {/* Ticket Usage Pill */}
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3.5 border border-white/20 min-w-[220px]">
                      <div className="flex justify-between text-xs text-blue-100 font-medium mb-1.5">
                        <span>Ticket Quota Usage</span>
                        <span className="font-bold text-white">
                          {customer.ticket_usage || 0} / {customer.limit_ticket || 0}
                        </span>
                      </div>
                      <div className="w-full bg-white/20 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-emerald-400 h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${Math.min(
                              (((customer.ticket_usage || 0) / (customer.limit_ticket || 1)) * 100),
                              100
                            )}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Counter Stats Bar */}
                <div className="grid grid-cols-3 divide-x divide-gray-100 border-t border-gray-100 bg-slate-50/60 text-center py-3.5 text-xs font-semibold text-gray-700">
                  <div className="flex items-center justify-center gap-2">
                    <FiUser className="w-4 h-4 text-blue-600" />
                    <span>{users.length} Users</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <FiUsers className="w-4 h-4 text-purple-600" />
                    <span>{groups.length} Groups</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <FiFolder className="w-4 h-4 text-amber-600" />
                    <span>{projects.length} GCP Projects</span>
                  </div>
                </div>
              </div>
            )}

            {/* Tabs Header */}
            <div className="flex border-b border-gray-200 bg-white rounded-t-xl px-4 pt-2 shadow-sm">
              <button
                onClick={() => setActiveTab("users")}
                className={`flex items-center gap-2 py-3.5 px-5 text-sm font-bold border-b-2 transition-colors ${
                  activeTab === "users"
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-800"
                }`}
              >
                <FiUser className="w-4 h-4" />
                Users
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                    activeTab === "users" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {users.length}
                </span>
              </button>

              <button
                onClick={() => setActiveTab("groups")}
                className={`flex items-center gap-2 py-3.5 px-5 text-sm font-bold border-b-2 transition-colors ${
                  activeTab === "groups"
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-800"
                }`}
              >
                <FiUsers className="w-4 h-4" />
                Groups
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                    activeTab === "groups" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {groups.length}
                </span>
              </button>

              <button
                onClick={() => setActiveTab("projects")}
                className={`flex items-center gap-2 py-3.5 px-5 text-sm font-bold border-b-2 transition-colors ${
                  activeTab === "projects"
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-800"
                }`}
              >
                <FiFolder className="w-4 h-4" />
                Projects
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                    activeTab === "projects" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {projects.length}
                </span>
              </button>
            </div>

            {/* TAB CONTENT: USERS */}
            {activeTab === "users" && (
              <div className="bg-white rounded-b-xl border border-t-0 border-gray-200 shadow-sm p-6 space-y-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <div className="relative w-full sm:w-72">
                    <FiSearch className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <button
                    onClick={() => setIsAddUserModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors shadow-sm"
                  >
                    <FiPlus className="w-4 h-4" />
                    Add User
                  </button>
                </div>

                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-gray-600 text-xs uppercase font-bold tracking-wider">
                      <tr>
                        <th className="px-6 py-3 text-left">User</th>
                        <th className="px-6 py-3 text-left">Role</th>
                        <th className="px-6 py-3 text-left">Contact</th>
                        <th className="px-6 py-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredUsers.length > 0 ? (
                        filteredUsers.map((u) => (
                          <tr key={u.id_user} className="hover:bg-gray-50/80 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="font-semibold text-gray-900">{u.full_name}</div>
                              <div className="text-xs text-gray-500">@{u.username}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full border ${getRoleBadge(
                                  u.role
                                )}`}
                              >
                                {u.role}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                              <div className="flex items-center gap-1.5">
                                <FiMail className="w-3.5 h-3.5 text-gray-400" />
                                <span>{u.email}</span>
                              </div>
                              {u.phone && (
                                <div className="flex items-center gap-1.5 mt-0.5 text-gray-500">
                                  <FiPhone className="w-3.5 h-3.5 text-gray-400" />
                                  <span>{u.phone}</span>
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <div className="flex justify-center items-center gap-2">
                                <button
                                  onClick={() => {
                                    setEditUser({
                                      id_user: u.id_user,
                                      full_name: u.full_name || "",
                                      username: u.username || "",
                                      email: u.email || "",
                                      phone: u.phone || "",
                                      role: u.role || "Customer",
                                      password: "",
                                    });
                                    setIsEditUserModalOpen(true);
                                  }}
                                  className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                                  title="Edit User"
                                >
                                  <FiEdit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setUserToDelete(u);
                                    setIsDeleteUserModalOpen(true);
                                  }}
                                  className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                                  title="Delete User"
                                >
                                  <FiTrash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center py-10 text-gray-500 text-sm">
                            No users found for this customer.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TAB CONTENT: GROUPS */}
            {activeTab === "groups" && (
              <div className="bg-white rounded-b-xl border border-t-0 border-gray-200 shadow-sm p-6 space-y-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <div className="relative w-full sm:w-72">
                    <FiSearch className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search groups..."
                      value={groupSearch}
                      onChange={(e) => setGroupSearch(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <button
                    onClick={() => setIsAddGroupModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors shadow-sm"
                  >
                    <FiPlus className="w-4 h-4" />
                    Add Group
                  </button>
                </div>

                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-gray-600 text-xs uppercase font-bold tracking-wider">
                      <tr>
                        <th className="px-6 py-3 text-left">Group Name</th>
                        <th className="px-6 py-3 text-left">Group ID</th>
                        <th className="px-6 py-3 text-center">Manage</th>
                        <th className="px-6 py-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredGroups.length > 0 ? (
                        filteredGroups.map((g) => (
                          <tr key={g.group_id} className="hover:bg-gray-50/80 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="font-semibold text-gray-900 flex items-center gap-2">
                                <div className="p-1.5 bg-blue-50 rounded-md text-blue-600">
                                  <FiUsers className="w-4 h-4" />
                                </div>
                                {g.group_name}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap font-mono text-xs text-gray-500">
                              {g.group_id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <button
                                onClick={() => navigate(`/groups/${g.group_id}`)}
                                className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold rounded-lg transition-colors border border-blue-200"
                              >
                                Members & Projects
                                <FiExternalLink className="w-3.5 h-3.5" />
                              </button>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <div className="flex justify-center items-center gap-2">
                                <button
                                  onClick={() => {
                                    setEditGroup({
                                      group_id: g.group_id,
                                      group_name: g.group_name,
                                    });
                                    setIsEditGroupModalOpen(true);
                                  }}
                                  className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                                  title="Edit Group"
                                >
                                  <FiEdit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setGroupToDelete(g);
                                    setIsDeleteGroupModalOpen(true);
                                  }}
                                  className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                                  title="Delete Group"
                                >
                                  <FiTrash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center py-10 text-gray-500 text-sm">
                            No groups found for this customer.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TAB CONTENT: PROJECTS */}
            {activeTab === "projects" && (
              <div className="bg-white rounded-b-xl border border-t-0 border-gray-200 shadow-sm p-6 space-y-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <div className="relative w-full sm:w-72">
                    <FiSearch className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search projects..."
                      value={projectSearch}
                      onChange={(e) => setProjectSearch(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  {customer?.billing_account_id && (
                    <button
                      onClick={handleSyncProjects}
                      disabled={isSyncingProjects}
                      className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm font-semibold transition-colors shadow-sm disabled:opacity-60"
                    >
                      <FiRefreshCw className={`w-4 h-4 ${isSyncingProjects ? "animate-spin" : ""}`} />
                      {isSyncingProjects ? "Syncing GCP..." : "Sync Projects from GCP"}
                    </button>
                  )}
                </div>

                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-gray-600 text-xs uppercase font-bold tracking-wider">
                      <tr>
                        <th className="px-6 py-3 text-left">Project Name</th>
                        <th className="px-6 py-3 text-left">Project ID</th>
                        <th className="px-6 py-3 text-left">Project Number</th>
                        <th className="px-6 py-3 text-center">Billing ID</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredProjects.length > 0 ? (
                        filteredProjects.map((p) => (
                          <tr key={p.project_id} className="hover:bg-gray-50/80 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="font-semibold text-gray-900 flex items-center gap-2">
                                <div className="p-1.5 bg-amber-50 rounded-md text-amber-600">
                                  <FiFolder className="w-4 h-4" />
                                </div>
                                {p.project_name || p.project_id}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap font-mono text-xs text-gray-600">
                              {p.project_id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap font-mono text-xs text-gray-500">
                              {p.project_number || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center font-mono text-xs text-gray-600">
                              {p.billing_account_id || customer?.billing_account_id || "-"}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center py-10 text-gray-500 text-sm">
                            No projects registered for this customer yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      </div>

      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      {/* ======================= ADD USER MODAL ======================= */}
      {isAddUserModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-slate-50/60">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FiUser className="w-5 h-5 text-blue-600" />
                Add User for {customer?.company_name}
              </h3>
              <button
                onClick={() => setIsAddUserModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddUserSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  required
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  placeholder="e.g. John Doe"
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    required
                    value={newUser.username}
                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                    placeholder="johndoe"
                    className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">
                    Role
                  </label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="Customer Admin">Customer Admin</option>
                    <option value="Customer">Customer</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="john@example.com"
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">
                    Phone
                  </label>
                  <input
                    type="text"
                    value={newUser.phone}
                    onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                    placeholder="08123456789"
                    className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    required
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="••••••••"
                    className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2.5 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsAddUserModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors shadow-sm"
                >
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================= EDIT USER MODAL ======================= */}
      {isEditUserModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-slate-50/60">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FiEdit className="w-5 h-5 text-blue-600" />
                Edit User
              </h3>
              <button
                onClick={() => setIsEditUserModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleEditUserSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  required
                  value={editUser.full_name}
                  onChange={(e) => setEditUser({ ...editUser, full_name: e.target.value })}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    required
                    value={editUser.username}
                    onChange={(e) => setEditUser({ ...editUser, username: e.target.value })}
                    className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">
                    Role
                  </label>
                  <select
                    value={editUser.role}
                    onChange={(e) => setEditUser({ ...editUser, role: e.target.value })}
                    className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="Customer Admin">Customer Admin</option>
                    <option value="Customer">Customer</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={editUser.email}
                  onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">
                    Phone
                  </label>
                  <input
                    type="text"
                    value={editUser.phone}
                    onChange={(e) => setEditUser({ ...editUser, phone: e.target.value })}
                    className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">
                    New Password <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="password"
                    value={editUser.password}
                    onChange={(e) => setEditUser({ ...editUser, password: e.target.value })}
                    placeholder="Leave blank to keep current"
                    className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2.5 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsEditUserModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors shadow-sm"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================= DELETE USER MODAL ======================= */}
      {isDeleteUserModalOpen && userToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-3 text-red-600">
              <div className="p-2.5 bg-red-100 rounded-full">
                <FiTrash2 className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Delete User</h3>
            </div>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete user{" "}
              <strong className="text-gray-900">{userToDelete.full_name}</strong> (@{userToDelete.username})?
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => {
                  setIsDeleteUserModalOpen(false);
                  setUserToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteUser}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition-colors shadow-sm"
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================= ADD GROUP MODAL ======================= */}
      {isAddGroupModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-slate-50/60">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FiUsers className="w-5 h-5 text-blue-600" />
                Add Group for {customer?.company_name}
              </h3>
              <button
                onClick={() => setIsAddGroupModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddGroupSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">
                  Group Name
                </label>
                <input
                  type="text"
                  required
                  value={newGroup.group_name}
                  onChange={(e) => setNewGroup({ ...newGroup, group_name: e.target.value })}
                  placeholder="e.g. Engineering, DevOps, Finance"
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex justify-end gap-2.5 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsAddGroupModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors shadow-sm"
                >
                  Create Group
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================= EDIT GROUP MODAL ======================= */}
      {isEditGroupModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-slate-50/60">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FiEdit className="w-5 h-5 text-blue-600" />
                Edit Group
              </h3>
              <button
                onClick={() => setIsEditGroupModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleEditGroupSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1">
                  Group Name
                </label>
                <input
                  type="text"
                  required
                  value={editGroup.group_name}
                  onChange={(e) => setEditGroup({ ...editGroup, group_name: e.target.value })}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex justify-end gap-2.5 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsEditGroupModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors shadow-sm"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================= DELETE GROUP MODAL ======================= */}
      {isDeleteGroupModalOpen && groupToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-3 text-red-600">
              <div className="p-2.5 bg-red-100 rounded-full">
                <FiTrash2 className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Delete Group</h3>
            </div>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete group{" "}
              <strong className="text-gray-900">{groupToDelete.group_name}</strong>?
              This will unassign all members and projects associated with this group.
            </p>
            <div className="flex justify-end gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => {
                  setIsDeleteGroupModalOpen(false);
                  setGroupToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteGroup}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition-colors shadow-sm"
              >
                Delete Group
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDetail;
